import { AccountPost, AccountPut, User } from "callable-data-objects";
import { GoogleASRLanguage } from "callable-data-objects/dist/enums/GoogleASRLanguage";
import { GoogleTTSLanguage } from "callable-data-objects/dist/enums/GoogleTTSLanguage";
import Account from "callable-data-objects/dist/models/account/Account";
import { AccountFilterRequest } from "callable-data-objects/dist/models/account/AccountFilterRequest";
import Page from "callable-data-objects/dist/services/Page";
import {
  AssetService,
  DDIService,
  DeviceService,
  EventGroupService,
  SettingService,
  TrunkService,
  WebhookService,
} from "callable-data-objects/dist/services/Services";
import { APIAccountContext } from "Providers/APIContexts";
import { APIAccountProps, DefaultProviderProps } from "Providers/types";
import React, { useEffect, useState } from "react";
import { useAPI } from "./APIProvider";

const APIAccountProvider = ({ children }: DefaultProviderProps) => {
  const api = useAPI();

  const [device, setDevice] = useState<DeviceService | undefined>();
  const [ddi, setDDI] = useState<DDIService | undefined>();
  const [asset, setAsset] = useState<AssetService | undefined>();
  const [trunk, setTrunk] = useState<TrunkService | undefined>();
  const [setting, setSetting] = useState<SettingService | undefined>();
  const [webhook, setWebhook] = useState<WebhookService | undefined>();
  const [events, setEvents] = useState<EventGroupService | undefined>();
  const [accounts, setAccounts] = useState<Page<Account> | undefined>();
  const [users, setUsers] = useState<User[]>([]);
  const [listAccounts, setListAccounts] = useState<Account[]>([]);
  const [account, setAccount] = useState<Account | undefined>();
  const [create, setCreate] = useState<AccountPost | undefined>();
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [filter, setFilter] = useState<AccountFilterRequest>({
    text: "",
    page: 0,
    max: 12,
    sortField: "name",
    sort: "ASC",
  });

  useEffect(() => {
    api.accountService.list().then((resp) => setListAccounts(resp));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filter) {
      api.accountService.filter(filter).then((resp) => setAccounts(resp));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  function search(text: string): void {
    setFilter({
      ...filter,
      text,
    });
  }

  async function select(id: string): Promise<void> {
    setSubmitting(true);
    const resp = await api.accountService.get(id);
    setAccount(resp);
    setDevice(api.accountService.device(id));
    setDDI(api.accountService.ddi(id));
    setTrunk(api.accountService.trunk(id));
    setAsset(api.accountService.asset(id));
    setSetting(api.accountService.setting(id));
    setWebhook(api.accountService.webhook(id));
    setEvents(api.accountService.events(id));
    getAccountUsers(resp.id);
    setSubmitting(false);
  }

  async function getAccountUsers(id: string) {
    console.log("getAccounts");
    api.userService.accountUsers(id).then((resp: User[]) => setUsers(resp));
  }

  function unselect() {
    setAccount(undefined);
    setDevice(undefined);
    setDDI(undefined);
    setTrunk(undefined);
    setSetting(undefined);
    setWebhook(undefined);
    setEvents(undefined);
  }

  function createAccount() {
    setCreate({
      organisation: { id: "", name: "" },
      name: "",
      domain: "",
      timezone: "Europe/London",
      countryCode: "gb",
      address: {
        line1: "",
        line2: "",
        level1: "",
        level2: "",
        postalCode: "",
        country: "England",
      },
      ttsLanguage: GoogleTTSLanguage.English_UK_Standard_C_Female,
      asrLanguage: GoogleASRLanguage.English_United_Kingdom,
    });
  }

  function uncreate() {
    setCreate(undefined);
  }

  function validateName(value: string): string | undefined {
    let error: string | undefined;
    if (value === null || value === "") error = "Name required";
    if (value.length < 3) error = "Name must be 3 or more characters";
    if (
      listAccounts &&
      listAccounts
        .filter((acc) => account === undefined || account.id !== acc.id)
        .map((acc) => acc.name)
        .indexOf(value) > -1
    ) {
      error = "Name not unique";
    }
    return error;
  }

  function validateDomain(value: string): string | undefined {
    let error: string | undefined;
    if (value === null || value === "") error = "Domain required";
    if (value.length < 3) error = "Domain must be 3 or more characters";
    if (value.length > 16) error = "Domain must be 16 or less characters";
    if (value.match(/[\W_]+/))
      error = "No special characters, spaces or underscores";
    if (
      listAccounts &&
      listAccounts
        .filter((acc) => account === undefined || account.id !== acc.id)
        .map((acc) => acc.domain)
        .indexOf(value) > -1
    ) {
      error = "Domain not unique";
    }
    return error;
  }

  async function save(payload: AccountPost) {
    setSubmitting(true);
    if (create) {
      const response = await api.accountService.post("", payload);
      if (response) {
        select(response.id);
        uncreate();
        search("");
      }
    }
    setSubmitting(false);
  }

  async function update(payload: AccountPut) {
    setSubmitting(true);
    await api.accountService.put(payload.id, payload);
    search("");
    select(payload.id);
    setSubmitting(false);
  }

  async function updateAddress(payload: AccountPut) {
    setSubmitting(true);
    await api.accountService.put(payload.id + "/address", payload);
    search("");
    select(payload.id);
    setSubmitting(false);
  }

  async function remove(id: string) {
    setSubmitting(true);
    await api.accountService.delete(id);
    search("");
    unselect();
    setSubmitting(false);
  }
  const accountProps: APIAccountProps = {
    accounts,
    account,
    listAccounts,
    filter,
    create,
    submitting,
    users,
    save,
    update,
    updateAddress,
    validateName,
    validateDomain,
    search,
    select,
    unselect,
    createAccount,
    uncreate,
    remove,
    setFilter,
    deviceService: device,
    ddiService: ddi,
    trunkService: trunk,
    webhookService: webhook,
    settingService: setting,
    eventGroupService: events,
    assetService: asset,
  };

  return (
    <APIAccountContext.Provider value={accountProps}>
      {children}
    </APIAccountContext.Provider>
  );
};

export default APIAccountProvider;
